import {CardHeader, ControlledInput, DashboardCard} from '@hm/fundamental'
import {useFormContext, Controller} from 'react-hook-form'

import {useTranslation} from '../../../../../hooks/useTranslation'
import {SelectGrouped, SelectGroupedOption} from '../../../../ManageUser/components/SelectGrouped'
import {useAllowedHConnectConfigCountries} from '../../hooks/useAllowedHConnectConfigCountries'
import {SlotManagementConfig} from '../types/SlotManagementConfig'

type Props = {
  isReadOnly: boolean
}

type DataScopeFields = Extract<
  keyof SlotManagementConfig,
  'countryCode' | 'businessLine' | 'orgUnitId' | 'dispatchGroup' | 'plantNumber'
>

type DataScopeFieldConfig = {
  field: DataScopeFields
  required?: boolean
  validate?: (
    value: string | undefined,
    formValues: SlotManagementConfig,
    t: (key: string) => string
  ) => string | true
  deps?: DataScopeFields[]
}

const DataScopeFields: DataScopeFieldConfig[] = [
  {
    field: 'businessLine',
    required: true
  },
  {
    field: 'orgUnitId',
    validate: (value, formValues, t) => {
      if (!value && formValues.dispatchGroup) {
        return t(
          'configurations.hconnect.slotManagement.validation.orgUnitRequiredForDispatchGroup'
        )
      }
      if (!value && formValues.plantNumber) {
        return t('configurations.hconnect.slotManagement.validation.orgUnitRequiredForPlantNumber')
      }
      return true
    },
    deps: ['dispatchGroup', 'plantNumber']
  },
  {
    field: 'dispatchGroup',
    validate: (value, formValues, t) => {
      if (!value && formValues.plantNumber) {
        return t(
          'configurations.hconnect.slotManagement.validation.dispatchGroupRequiredForPlantNumber'
        )
      }
      return true
    },
    deps: ['orgUnitId', 'plantNumber']
  },
  {
    field: 'plantNumber',
    deps: ['dispatchGroup', 'orgUnitId']
  }
]

export const SlotManagementConstraints = ({isReadOnly}: Props) => {
  const {t} = useTranslation()
  const {control} = useFormContext<SlotManagementConfig>()
  const countries = useAllowedHConnectConfigCountries()

  // Transform countries to SelectGroupedOption format
  const countryOptions: SelectGroupedOption[] = countries.map((country) => ({
    label: country,
    value: country
  }))

  return (
    <DashboardCard>
      <CardHeader>
        {t('configurations.hconnect.slotManagement.configurationDetails.constraints.title')}
      </CardHeader>
      <div className="flex flex-col gap-4">
        <Controller
          name="countryCode"
          control={control}
          rules={{
            required: t(
              'configurations.hconnect.slotManagement.configurationDetails.requiredField',
              {
                fieldName: t(
                  'configurations.hconnect.slotManagement.configurationDetails.countryCodeLabel'
                )
              }
            )
          }}
          render={({field: controllerField, fieldState}) => (
            <SelectGrouped
              inputLabel={t(
                'configurations.hconnect.slotManagement.configurationDetails.countryCodeLabel'
              )}
              {...controllerField}
              options={countryOptions}
              disabled={isReadOnly}
              error={fieldState.error?.message}
              emptyLabel={t(
                'configurations.hconnect.slotManagement.configurationDetails.countryCodeLabel'
              )}
              data-test-id="slot-management-configurations-country-code"
            />
          )}
        />
        {DataScopeFields.map((fieldConfig) => (
          <Controller
            key={fieldConfig.field}
            name={fieldConfig.field}
            control={control}
            rules={{
              required: fieldConfig.required
                ? t('configurations.hconnect.slotManagement.configurationDetails.requiredField', {
                    fieldName: t(
                      `configurations.hconnect.slotManagement.configurationDetails.${fieldConfig.field}Label`
                    )
                  })
                : false,
              validate: fieldConfig.validate
                ? (value, formValues) => fieldConfig.validate!(value, formValues, t)
                : undefined,
              deps: fieldConfig.deps
            }}
            render={({field: controllerField, fieldState}) => (
              <ControlledInput
                {...controllerField}
                label={t(
                  `configurations.hconnect.slotManagement.configurationDetails.${fieldConfig.field}Label`
                )}
                isReadOnly={isReadOnly}
                error={fieldState.error?.message}
                isTouched={fieldState.invalid}
                data-test-id={`slot-management-configurations-${fieldConfig.field}`}
              />
            )}
          />
        ))}
      </div>
    </DashboardCard>
  )
}
